/**
 * journalTheme — shared style tokens for the JournalScene + tab modules.
 *
 * Phaser-free. Centralising the palette + font choices here keeps the six
 * tab modules from each redeclaring the same constants and lets the scene
 * shell stay under its 350 LOC budget.
 *
 * Hand-lettered typography vibe per GDD §14.3 — Almendra serif, the same
 * face used by TitleScene + SleepOverlayScene + PauseOverlay.
 */

import {
  C5_BAND,
  C5_HANKO,
  C5_INK,
  C5_INK_DIM,
  C5_PAPER,
  C5_PAPER_DK,
  C5_PAPER_HI,
  C5_VEIL_ALPHA,
  hexToCSS,
} from '../ui/palette';

/** Painterly serif used for all journal chrome. Loaded by `index.html`. */
export const JOURNAL_FONT = "'Almendra', serif";

/** C5 "Indigo & Ink" (docs/design/ui-c5-indigo-ink.md): the journal is a paper
 *  spread, so its text is ink on cream and its backdrop is the indigo veil.
 *  Every value maps from a palette token — no ad-hoc hex here. */
export const JOURNAL_INK = hexToCSS(C5_INK);
export const JOURNAL_INK_HOVER = hexToCSS(C5_HANKO);
export const JOURNAL_INK_MUTED = hexToCSS(C5_INK_DIM);
export const JOURNAL_BG_DEEP = hexToCSS(C5_BAND);

/** Paper materials for the spread + the pinned slips each tab renders. */
export const JOURNAL_PAPER = C5_PAPER;
export const JOURNAL_PAPER_HI = C5_PAPER_HI;
export const JOURNAL_PAPER_EDGE = C5_PAPER_DK;
export const JOURNAL_SLIP_BG = C5_PAPER_HI;
export const JOURNAL_SLIP_BORDER = C5_INK_DIM;
export const JOURNAL_PIN = C5_HANKO;
export const JOURNAL_VEIL = C5_BAND;
export const JOURNAL_VEIL_ALPHA = C5_VEIL_ALPHA;

/** Layout constants — kept in one place so the scene + tab renderers
 *  agree on the same column widths. All values are in the reference
 *  resolution (960×540) coordinate space. */
export const JOURNAL_LAYOUT = {
  /** Width of the tab strip + content column. */
  CONTENT_WIDTH: 720,
  /** Y position where the tab strip baseline sits. */
  TAB_STRIP_Y: 80,
  /** Y position where the content scroll area begins. */
  CONTENT_TOP_Y: 120,
  /** Y position where the content scroll area ends. */
  CONTENT_BOTTOM_Y: 500,
  /** Vertical spacing between rendered lines in a tab. */
  LINE_SPACING: 14,
  /** Vertical room reserved per line block (heading + body). */
  LINE_BLOCK_HEIGHT: 88,
  /** Horizontal padding inside the content column. */
  CONTENT_PAD_X: 24,
} as const;

/** Font-size tokens — single source of truth so the cohesive feel
 *  survives later art-pass tweaks. */
export const JOURNAL_FONT_SIZE = {
  TITLE: '36px',
  TAB: '18px',
  HEADING: '16px',
  BODY: '15px',
  FOOTNOTE: '12px',
  EMPTY_STATE: '17px',
} as const;
